import React, { useEffect, useMemo } from 'react';
import { groupBy } from 'lodash';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { Field, formValues, change } from 'redux-form';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import TPGrid from 'components/TP-UI/TPGrid';
import TPSelect from 'components/TP-UI/TPSelect';
import TPAutocomplete from 'components/TP-UI/TPAutocomplete';
import TPTypography from 'components/TP-UI/TPTypography';
import { required } from 'utils/validation/fieldValidationRules';
import { ACCOUNT_TYPE_PRO } from 'modules/tradingCalculators/constants';

const ALL = 'All';
const retailRe = /[.]r$/;

const SymbolGroupForm = ({
  form,
  symbols,
  symbolGroup,
  symbol,
  accountType,
  contractSize,
  hideContractSize = false,
}) => {
  const { t } = useTranslation('tradingCalculators');
  const dispatch = useDispatch();
  const currencies = useMemo(() => {
    return symbols
      .filter((symbol) => symbolGroup === ALL || symbolGroup === symbol.symbolGroup)
      .filter((symbol) => {
        // account type is only available for margin calculator
        // for other page it's undefined and we should show all symbols
        if (!accountType || accountType === ACCOUNT_TYPE_PRO) {
          return !retailRe.test(symbol.symbol);
        }

        return retailRe.test(symbol.symbol);
      })
      .map(({ symbol }) => ({ value: symbol, label: symbol }));
  }, [symbols, symbolGroup, accountType]);

  useEffect(() => {
    if (!symbolGroup) {
      dispatch(change(form, 'symbolGroup', ALL));
    }

    dispatch(change(form, 'symbol', null));
  }, [symbolGroup, dispatch, form]);

  const groups = useMemo(() => {
    return [
      { value: ALL, label: ALL },
      ...Object.keys(groupBy(symbols, 'symbolGroup')).map((symbolGroup) => ({
        value: symbolGroup,
        label: symbolGroup,
      })),
    ];
  }, [symbols]);

  return (
    <TPGrid item container spacing={2}>
      <TPGrid item xs={12} md={6}>
        <Field
          label={t('fields.group')}
          name="symbolGroup"
          component={TPSelect}
          fullWidth
          options={groups}
          required
          validate={[required]}
        />
      </TPGrid>
      <TPGrid item xs={12} md={6}>
        <Field
          label={t('fields.symbol')}
          placeholder={t('placeholders.symbol')}
          name="symbol"
          component={TPAutocomplete}
          selectedValue={symbol}
          options={currencies}
          fullWidth
          required
          validate={[required]}
          hint={
            !hideContractSize ? (
              <>
                {`${t('fields.contractSize')}: `}
                <TPTypography component="span" variant="inherit" color="primary">
                  {contractSize ? `1 ${t('results.lot')} = ${contractSize}` : '\u2014'}
                </TPTypography>
              </>
            ) : null
          }
        />
      </TPGrid>
    </TPGrid>
  );
};

SymbolGroupForm.propTypes = {
  form: PropTypes.string.isRequired,
  symbols: PropTypes.arrayOf(PropTypes.object).isRequired,
  contractSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hideContractSize: PropTypes.bool,
};

export default compose(
  formValues('accountType'),
  formValues('symbolGroup'),
  formValues('symbol'),
)(SymbolGroupForm);
